/** Skip an offboarding item with a reason (required — the backend answers 400 for a blank one).
 *  The reason is kept with the item (`context.skip_reason`), in its history and in the audit log;
 *  a skipped item no longer blocks a release. A refusal is put in words: an item done or skipped
 *  meanwhile, or a tenant reopened meanwhile, turns Skip off and re-reads the list. */
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import { items, skipErrorOf, type PreflightItem, type PreflightStatus } from '@/api/preflight';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';
import { STATUS_LABELS } from '@/features/preflight/status';

import { releaseBlockingReason } from './offboardingRelease';

/** The backend's limit (`ConsolePreflightSkipRequest.reason`). */
export const SKIP_REASON_MAX = 2000;

const Schema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, 'Say why it is not done — the reason is kept with the item and in the audit log.')
    .max(SKIP_REASON_MAX, `At most ${SKIP_REASON_MAX} characters.`),
});
type Values = z.infer<typeof Schema>;

/** A refused skip in words (409), or the request's own message. */
export function skipErrorWords(e: unknown): string {
  const refusal = skipErrorOf(e);
  if (refusal?.code === 'superseded') {
    return 'The tenant was reopened, which set this item aside: it takes no change until the tenant is closed again.';
  }
  if (refusal?.code === 'not_offboarding') return 'Only an offboarding item can be skipped.';
  if (refusal?.code === 'tenant_not_closed') {
    return 'The tenant is active again: offboarding items can be skipped only while it is closed.';
  }
  if (refusal?.code === 'not_skippable') {
    const now =
      refusal.status && refusal.status in STATUS_LABELS
        ? STATUS_LABELS[refusal.status as PreflightStatus].toLowerCase()
        : 'no longer open';
    return `This item is ${now} now: only open work (pending, started or error) can be skipped.`;
  }
  return e instanceof ConsoleApiError ? e.message : 'Couldn’t skip the item.';
}

/** The 400 `{reason: [message]}` of a blank reason, or null. */
function reasonErrorOf(e: unknown): string | null {
  if (!(e instanceof ConsoleApiError) || e.status !== 400) return null;
  const reason = (e.body as { reason?: unknown } | undefined)?.reason;
  if (Array.isArray(reason) && typeof reason[0] === 'string') return reason[0];
  return typeof reason === 'string' ? reason : null;
}

interface Props {
  topId: number;
  /** The item to skip; null keeps the dialog closed. */
  item: PreflightItem | null;
  onClose: () => void;
}

export function SkipOffboardingItemDialog({ topId, item, onClose }: Props) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const { register, handleSubmit, reset, setError, formState } = useForm<Values>({
    resolver: zodResolver(Schema),
    defaultValues: { reason: '' },
  });
  // The per-TO item lists: both checklists share the prefix.
  const refresh = () => void qc.invalidateQueries({ queryKey: ['preflight', 'items', topId] });

  const mut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => items.skip(topId, id, reason),
    onSuccess: () => {
      snackbar.show('Item skipped — the reason is kept with it.', 'success');
      refresh();
      onClose();
    },
    onError: (e) => {
      const fieldError = reasonErrorOf(e);
      if (fieldError) setError('reason', { message: fieldError });
      // Decided meanwhile (done, skipped or reopened): show where it stands now.
      if (skipErrorOf(e)) refresh();
    },
  });

  const { reset: resetMutation } = mut;
  useEffect(() => {
    if (!item) return;
    reset({ reason: '' });
    resetMutation();
  }, [item, reset, resetMutation]);

  const refused = skipErrorOf(mut.error) != null;
  const showError = mut.error != null && reasonErrorOf(mut.error) == null;
  const submit = handleSubmit((v) => item && mut.mutate({ id: item.id, reason: v.reason }));
  const blocksRelease = item != null && releaseBlockingReason(item) != null;

  return (
    <AppDialog
      open={item != null}
      onClose={onClose}
      title="Skip the item"
      disableBackdropClose={mut.isPending || formState.isDirty}
      actions={
        <>
          <Button onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={mut.isPending || refused}
            onClick={() => void submit()}
            startIcon={mut.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Skip item
          </Button>
        </>
      }
    >
      {item && (
        <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {item.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Skipping records that this item will not be done. The reason is kept with the item, in
            its history and in the audit log.
            {blocksRelease && ' A skipped item no longer blocks the release of the code.'}
          </Typography>
          <TextField
            {...register('reason')}
            label="Reason"
            required
            multiline
            minRows={2}
            size="small"
            autoFocus
            disabled={mut.isPending}
            inputProps={{ maxLength: SKIP_REASON_MAX }}
            error={Boolean(formState.errors.reason)}
            helperText={
              formState.errors.reason?.message ??
              'Why it is not done — e.g. the numbers are kept in case the tenant comes back.'
            }
          />
          {showError && <Alert severity="error">{skipErrorWords(mut.error)}</Alert>}
        </Box>
      )}
    </AppDialog>
  );
}
